/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { format, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classNames from 'classnames';
import * as React from 'react';

import { ISalesLine } from './get-sales-line';
import { groupSalesLines, IDeliveryType, ISalesStatusCode } from './utilities';

export interface IGroupDelivery {
    deliveryProps: INodeProps;
    pickupProps: INodeProps;
    showTimeslot: boolean;
    heading: React.ReactNode;
    count: React.ReactNode;
    processing: React.ReactNode;
    trackingInfo: React.ReactNode;
    address: React.ReactNode;
    shippingItemsToYou?: React.ReactNode;
    pickupDateTimeslot?: React.ReactNode;
    shippedStatusCheck?: React.ReactNode;
}

export interface IGroup {
    groupProps: INodeProps;
    salesLinesProps: INodeProps;
    delivery: IGroupDelivery;
    salesLines: ISalesLine[];
    address: React.ReactNode;

    // Row data
    data: {
        count: number;
        deliveryType: IDeliveryType;
        salesStatus: ISalesStatusCode;
    };
    isCashAndCarryTransaction?: boolean;
}

export interface IGroupsInput {
    moduleName: string;
    salesLines: ISalesLine[];
    resources: {
        orderItemLabel: string;
        orderItemsLabel: string;
        pickupItemsHeader?: string;
        pickingItemsLabel?: string;
        shippingAddressTitle?: string;
        shippingItemToYouLabel?: string;
        shippingItemToOBOCustomerLabel?: string;
        emailItemsHeader?: string;
        emailingItemsLabel?: string;
    };
    showAddress?: boolean;
    isConfirmationView?: boolean;
    showTimeslot?: boolean;
    retailMultiplePickUpOptionEnabled?: boolean;
    isCashAndCarryTransaction?: boolean;
    isSelectionModeEnabled?: boolean;
    isOboRequest?: boolean;
    customerName?: string;
}

export interface IGroups {
    groupsProps: INodeProps;
    groups: IGroup[];
}

/**
 * Creates sales lines node props object.
 * @param moduleName - Module name to use as a class name prefix.
 * @param isSelectionModeEnabled - Specifies if the selection mode is enabled for the sales lines.
 * @returns Sales lines node props with the class name to use for the sales lines container.
 */
const getSalesLinesProps: (moduleName: string, isSelectionModeEnabled?: boolean) => INodeProps = (
    moduleName: string,
    isSelectionModeEnabled?: boolean
): INodeProps => {
    const className = `${moduleName}__sales-lines`;
    const salesLinesProps: INodeProps = {
        className: classNames(
            className,
            isSelectionModeEnabled ? `${className}__selection-global-enabled` : `${className}__selection-global-disabled`
        )
    };
    return salesLinesProps;
};

export const getGroups = ({
    moduleName,
    salesLines,
    showAddress,
    showTimeslot,
    isConfirmationView,
    retailMultiplePickUpOptionEnabled,
    isCashAndCarryTransaction,
    isSelectionModeEnabled,
    customerName,
    isOboRequest,
    resources: {
        orderItemLabel,
        orderItemsLabel,
        pickupItemsHeader,
        pickingItemsLabel,
        shippingAddressTitle,
        shippingItemToYouLabel,
        shippingItemToOBOCustomerLabel,
        emailItemsHeader,
        emailingItemsLabel
    }
}: IGroupsInput): IGroups => {
    const groups = groupSalesLines(salesLines).map(group => {
        const { deliveryType, salesStatus, count, salesLines: _salesLines } = group;
        const itemLabel = count === 1 ? orderItemLabel : orderItemsLabel;
        const pickingItemsLabelText = pickingItemsLabel && format(pickingItemsLabel, count, itemLabel);
        const shippingItemsToText = isOboRequest
            ? shippingItemToOBOCustomerLabel && format(shippingItemToOBOCustomerLabel, count, itemLabel, customerName)
            : shippingItemToYouLabel && format(shippingItemToYouLabel, count, itemLabel);
        const emailingItemsLabelText = emailingItemsLabel && format(emailingItemsLabel, count, itemLabel);
        const multiplePickup = deliveryType === 'ship' ? 'ship' : deliveryType === 'email' ? 'email' : 'pickup';
        const groupProps = { className: `${moduleName}__group-deliveryType-${multiplePickup} salesStatus-${salesStatus}` };

        const salesLinesProps = getSalesLinesProps(moduleName, isSelectionModeEnabled);
        const address = _salesLines[0].address;
        const trackingInfo = _salesLines[0].trackingInfo;
        const isProcessing = _salesLines[0].data.salesStatus === 'processing';
        const pickDateTimeslot = _salesLines[0].pickupDateTimeslot;
        const shippedStatusCheck = _salesLines[0].data.salesStatus;
        const multiplePickUp = retailMultiplePickUpOptionEnabled ? 'multiple-pickUp' : '';
        const delivery: IGroupDelivery = {
            deliveryProps: { className: `${moduleName}__group-delivery ${multiplePickUp}` },
            pickupProps: { className: `${moduleName}__delivery-container` },
            heading: (
                <span className={`${moduleName}__group-delivery-heading`}>
                    {isProcessing
                        ? isConfirmationView
                            ? deliveryType === 'pickUp'
                                ? pickupItemsHeader
                                : deliveryType === 'ship'
                                ? shippingAddressTitle
                                : deliveryType === 'email'
                                ? emailItemsHeader
                                : deliveryType
                            : _salesLines[0].deliveryType
                        : _salesLines[0].salesStatus}
                </span>
            ),
            processing: isProcessing ? (
                <span className={`${moduleName}__group-delivery-processing-label`}>{_salesLines[0].salesStatus}</span>
            ) : (
                undefined
            ),
            count: !isConfirmationView && <span className={`${moduleName}__group-delivery-total-items`}>{`(${count} ${itemLabel})`}</span>,
            shippingItemsToYou:
                isConfirmationView && deliveryType === 'ship' ? (
                    <span className={`${moduleName}__sales-lines-shipping-items-label`}>{shippingItemsToText}</span>
                ) : deliveryType === 'pickUp' ? (
                    <span className={`${moduleName}__sales-lines-picking-items-label`}>{pickingItemsLabelText}</span>
                ) : deliveryType === 'email' ? (
                    <span className={`${moduleName}__sales-lines-emailing-items-label`}>{emailingItemsLabelText}</span>
                ) : (
                    <span className={`${moduleName}__sales-lines-picking-items-label`}>{pickingItemsLabelText}</span>
                ),
            trackingInfo,
            address,
            pickupDateTimeslot: pickDateTimeslot,
            showTimeslot: showTimeslot || false,
            shippedStatusCheck
        };
        return {
            groupProps,
            salesLinesProps,
            delivery,
            salesLines: _salesLines,
            address: showAddress && address,
            data: {
                count,
                deliveryType,
                salesStatus
            },
            isCashAndCarryTransaction
        };
    });

    return {
        groupsProps: { className: `${moduleName}__groups` },
        groups
    };
};
